import { useRef, useState } from 'react';
import { useToast } from '../../hooks/useToast';
import { useConfirm } from '../../hooks/useConfirm';
import { useBusy, useBusyKeys } from '../../hooks/useBusy';
import { useAfterWrite } from '../../hooks/useAfterWrite';
import { Link } from 'react-router-dom';
import { useListFrom } from '../../hooks/useUrlParam';
import { deleteBout } from '../../api/client';
import { apiError, formatWarnings, type ApiWarning } from '../../hooks/useApiCall';
import { useI18n, formatDay } from '../../i18n';
import { useLabels } from '../../i18n/labels';
import { useAuthStore } from '../../store/auth';
import GenderBadge from '../../components/shared/GenderBadge';
import EmptyState from '../../components/shared/EmptyState';
import BoutLevelPill from '../../components/bout/BoutLevelPill';
import BoutStatusPill from '../../components/bout/BoutStatusPill';
import StagePill from '../../components/bout/StagePill';
import BoutFormModal from '../../components/tournament/BoutFormModal';
import BoutListPrint from '../../components/bout/BoutListPrint';
import { PencilIcon, BellIcon, LockIcon, HourglassIcon, ClockIcon, DotIcon, ClipboardIcon, CalendarIcon, WarningIcon, ShuffleIcon, PrinterIcon, SwapIcon } from '../../components/shared/Icons';
import ModalBackdrop from '../../components/shared/ModalBackdrop';
import ModalHeader from '../../components/shared/ModalHeader';
import ModalFooter from '../../components/shared/ModalFooter';
import GuestBellPrompt from '../../components/bout/GuestBellPrompt';
import { followBout, unfollowBout } from '../../lib/boutBell';
import type { ManualBoutRow } from '../../components/tournament/ManualBoutAssignModal';
import type { OpenRingPreviewData } from '../../components/tournament/OpenRingMatchPreviewModal';
import type {
  Tournament, Bout, Bracket, Registration, Ring, JuryAssignment,
  TournamentReferee, AgeGroup,
} from '../../types';

// ── Bout time formula ────────────────────────────────────────────────────────
import { MAX_SIDE_JUDGES, MIN_SIDE_JUDGES, deriveAgeGroup, deriveWeightClass, roundFormula } from '../../utils/tournament';
import { runsAsOpenRing, hasChampionshipPart, drawsAfterCheckin } from '../../utils/tournamentType';
import { boutSeatsOf } from '../../utils/boutSeats';
import { boutScoreState, managesUnlocked } from '../../utils/tournamentState';
import { successText } from '../../i18n/labels';
import { BADGE_XS } from '../../components/shared/badgeSize';

interface BoutsTabProps {
  id: string;
  tournament: Tournament;
  bouts: Bout[];
  /** #605 — a delete filters the newest list, not the list of the click. The write joins the
   *  order guard of the hook (#664): a fetch in flight does not bring the bout back. */
  patchBouts: (fn: (prev: Bout[]) => Bout[]) => void;
  brackets: Bracket[];
  registrations: Registration[];
  referees: TournamentReferee[];
  juryAssignments: JuryAssignment[];
  rings: Ring[];
  tournamentDays: string[];
  isMultiDay: boolean;
  managesThisTournament: boolean;
  canOfficiate: boolean;
  isJury: boolean;
  isInRefereePool: boolean;
  boutSubs: Set<string>;
  /** Shows a tap of the bell at once; call the returned function when the request answered. */
  setBoutSub: (boutId: string, on: boolean) => () => void;
  ageGroups: AgeGroup[];
  setOpenRingPairs: (v: OpenRingPreviewData | null) => void;
  setManualBouts: (v: ManualBoutRow[] | null) => void;
  setManualRings: (v: Ring[]) => void;
  setManualDays: (v: string[]) => void;
  setShowManualAssign: (v: boolean) => void;
  setShowOfficialsPlan: (v: boolean) => void;
  reload: () => Promise<void>;
}

export default function BoutsTab({
  id, tournament, bouts, patchBouts, brackets, registrations, referees,
  juryAssignments, rings, tournamentDays, isMultiDay,
  managesThisTournament, canOfficiate, isJury, isInRefereePool,
  boutSubs, setBoutSub, ageGroups,
  setOpenRingPairs, setManualBouts, setManualRings, setManualDays, setShowManualAssign,
  setShowOfficialsPlan,
  reload, }: BoutsTabProps) {
  const { showError, showSuccess, showWarning, showToast } = useToast();
  const { confirm } = useConfirm();
  const afterWrite = useAfterWrite();
  // #595 — the bout page goes back to this list with its position.
  const listFrom = useListFrom();
  const { t } = useI18n();
  const { statusLabel, boutCount } = useLabels();
  const { user, hasRole } = useAuthStore();
  const [showGuestBell, setShowGuestBell] = useState(false); // #515
  // #593 — the bouts whose bell request runs: a second tap waits for the answer.
  const bellBusy = useRef(new Set<string>());
  // #605 — the locks of Match Boxers and Reset Bouts: a second tap sends no copy.
  const matching = useBusy();
  const resetting = useBusy();
  // #605 — a lock for each bout: the modal closes at once, so a delete of
  // another bout must not wait for this one.
  const deletes = useBusyKeys();

  const [boutFormOpen, setBoutFormOpen] = useState(false);
  const [boutFormTarget, setBoutFormTarget] = useState<Bout | null>(null);
  const [boutFormMode, setBoutFormMode] = useState<'create' | 'edit'>('create');
  const [showPrintModal, setShowPrintModal] = useState(false);
  const [printSelectedDays, setPrintSelectedDays] = useState<string[]>([]);
  const [printSelectedRings, setPrintSelectedRings] = useState<string[]>([]);

  const STATUS_ORDER: Record<string, number> = { current: 0, pending: 1, scored: 2 };
  const boutsOrdered = [...bouts].sort((a, b) => {
    const sd = (STATUS_ORDER[a.status] ?? 1) - (STATUS_ORDER[b.status] ?? 1);
    if (sd !== 0) return sd;
    if (a.bout_date && b.bout_date && a.bout_date !== b.bout_date) return a.bout_date < b.bout_date ? -1 : 1;
    if (a.bout_date && !b.bout_date) return 1;
    if (!a.bout_date && b.bout_date) return -1;
    return a.bout_number - b.bout_number;
  });
  const firstScoredIdx = boutsOrdered.findIndex(b => b.status === 'scored');

  // This function asks no question. The bout form asks both questions of a
  // hard delete, for this tab and for the bout page.
  async function handleDeleteBout(boutId: string) {
    await deletes.run(boutId, async () => {
      try {
        await deleteBout(boutId);
        patchBouts(prev => prev.filter(b => b.id !== boutId));
        showSuccess(t.tournament.boutDeleted);
      } catch (err) { showError(err); }
    });
  }

  // #536 — who may add a bout by hand or with Match Boxers. A championship:
  // only before its brackets get their bouts, as before. The open-ring rules
  // (an open ring, a hybrid): at any time, but not while a bracket waits for
  // its plan, because the plan confirm refuses when any bout exists. A plain
  // open ring has no bracket, so its buttons behave as before.
  const bracketPlanPending = brackets.some(b => b.status === 'draft') || tournament.bout_plan_status === 'draft';
  const canAddBouts = managesUnlocked(tournament, managesThisTournament) && (runsAsOpenRing(tournament.type)
    ? !bracketPlanPending
    : !brackets.some(b => b.status === 'scheduled'));
  const showPlanFirst = managesUnlocked(tournament, managesThisTournament) && runsAsOpenRing(tournament.type) && bracketPlanPending;
  // #547 — a hybrid draws its brackets after the check-in (operator,
  // 2026-10-03). Until the bracket plan is confirmed, a bout made here can
  // take athletes who belong in a bracket, and any bout makes every later
  // bracket confirm refuse (BOUTS_ALREADY_EXIST). The manager confirms once:
  // a warning, not a block, so a hybrid with no bracket at all still works.
  // Once a bout exists, the brackets are blocked already: no second question.
  const weighInFirst = drawsAfterCheckin(tournament.type);
  const confirmBeforePlan = async (): Promise<boolean> =>
    !weighInFirst || tournament.bout_plan_status === 'confirmed' || bouts.length > 0
    || confirm({ message: t.tournament.hybridNoPlanConfirm, confirmLabel: t.common.continue });
  // #547 — the way back from such a bout. "Reset Bouts & Unlock" is on the
  // Brackets tab, which shows only when a bracket exists.
  const showBoutsReset = managesUnlocked(tournament, managesThisTournament) && weighInFirst
    && brackets.length === 0 && bouts.length > 0 && !boutScoreState(bouts).any;

  return (
    <div className="space-y-4">
      {isJury && isInRefereePool && boutScoreState(bouts).open > 0 && (
        <div className="card p-4 bg-purple-50 border border-purple-200 no-print">
          <div className="font-semibold text-sm text-purple-800 inline-flex items-center gap-1.5"><ClipboardIcon className="w-4 h-4" /> {t.tournament.juryAssignTitle}</div>
          {/* #552 — the Claim button is on the bout page, not on a card of this list. */}
          <div className="text-xs text-purple-600 mt-1">{t.tournament.juryAssignHint}</div>
        </div>
      )}
      {isJury && !isInRefereePool && !managesThisTournament && boutScoreState(bouts).open > 0 && (
        <div className="card p-4 bg-amber-50 border border-amber-200 no-print">
          <div className="font-semibold text-sm text-amber-800 inline-flex items-center gap-1.5"><WarningIcon className="w-4 h-4" /> {t.tournament.joinPoolFirst}</div>
          <div className="text-xs text-amber-600 mt-1">{t.tournament.joinPoolFirstHint}</div>
        </div>
      )}
      {(canAddBouts || showPlanFirst || bouts.length > 0) && (
        <div className="flex gap-2 flex-wrap items-center no-print">
          {showPlanFirst && (
            <span data-testid="hybrid-plan-first" className="text-xs text-amber-700">{t.tournament.hybridPlanFirst}</span>
          )}
          {canAddBouts && (
            <>
              <button onClick={async () => {
                if (!(await confirmBeforePlan())) return;
                setBoutFormMode('create'); setBoutFormTarget(null); setBoutFormOpen(true);
              }} data-testid="bout-create-btn" className="btn-primary btn-sm">
                + {t.boutForm.createBout}
              </button>
              {runsAsOpenRing(tournament.type) && (
                <button
                  onClick={async () => {
                    // #665 — ask before run(): the question must not hold the lock.
                    if (!(await confirmBeforePlan())) return;
                    await matching.run(async () => {
                    try {
                      const { openRingPreview } = await import('../../api/client');
                      const res = await openRingPreview(id);
                      // Cast: api/client types pairs/unmatched as
                      // Record<string, unknown>[]; the modal consumes the
                      // richer OpenRingPreviewData shape. Narrowing lives at
                      // the boundary, not in api/client.
                      setOpenRingPairs(res.data as unknown as OpenRingPreviewData);
                      // #605 — the same-team pairs (#205) come in `warnings`. Nothing showed them.
                      const advice = formatWarnings((res.data as { warnings?: ApiWarning[] }).warnings);
                      if (advice) showWarning(advice);
                    } catch (err) { showError(err); }
                    });
                  }}
                  disabled={matching.busy}
                  className="btn-outline btn-sm inline-flex items-center gap-1"><ShuffleIcon className="w-3 h-3" /> {t.tournament.matchFighters}</button>
              )}
            </>
          )}
          {showBoutsReset && (
            <button data-testid="bouts-reset" onClick={async () => {
              // #665 — ask before run(): the question must not hold the lock.
              if (!(await confirm({ message: t.tournament.resetConfirm, confirmLabel: t.tournament.resetBouts, destructive: true }))) return;
              await resetting.run(async () => {
                let done: string;
                try {
                  const { resetBouts } = await import('../../api/client');
                  done = successText((await resetBouts(id)).data);
                } catch (err) { showError(err); return; }
                // #419 — the reset committed. A failed refetch is a warning, not an error.
                await afterWrite({ source: 'BoutsTab:reset', success: done, refresh: reload });
              });
            }} disabled={resetting.busy} className="btn-outline-red btn-sm">{t.tournament.resetBouts}</button>
          )}
          {bouts.length > 0 && managesUnlocked(tournament, managesThisTournament) && (
            <button
              onClick={() => {
                setManualBouts(bouts.map<ManualBoutRow>(b => ({
                  bout_id: b.id, bracket_id: b.bracket_id ?? '', bout_number: b.bout_number,
                  ring_id: b.ring_id, bout_date: b.bout_date ? String(b.bout_date).slice(0, 10) : '',
                  stage: b.stage, gender: b.red_gender || b.blue_gender,
                  age_group: b.bout_age_group, weight_class: b.bout_weight_class,
                  red_first_name: b.red_first_name, red_last_name: b.red_last_name,
                  blue_first_name: b.blue_first_name, blue_last_name: b.blue_last_name,
                  red_source_bout_id: b.red_source_bout_id, blue_source_bout_id: b.blue_source_bout_id,
                })).sort((a, bb) => (a.bout_number ?? 0) - (bb.bout_number ?? 0)));
                setManualRings(rings);
                setManualDays(tournamentDays);
                setShowManualAssign(true);
              }}
              className="btn-outline btn-sm inline-flex items-center gap-1">
              <SwapIcon className="w-3 h-3" /> {t.tournament.reassignBouts}
            </button>
          )}
          {bouts.length > 0 && (
            <>
              <div className="flex-1" />
              <button onClick={() => {
                const days = [...new Set(bouts.map(b => b.bout_date ? String(b.bout_date).slice(0, 10) : '').filter((s): s is string => !!s))].sort();
                const ringNames = [...new Set(bouts.map(b => b.ring_name).filter((s): s is string => !!s))].sort();
                setPrintSelectedDays(days);
                setPrintSelectedRings(ringNames);
                setShowPrintModal(true);
              }} className="btn-outline btn-sm inline-flex items-center gap-1"><PrinterIcon className="w-3 h-3" /> {t.tournament.printBouts}</button>
            </>
          )}
        </div>
      )}
      {/* Print modal — select days */}
      {showPrintModal && (
        // #552 — the shared shell; ModalBackdrop is no-print.
        <ModalBackdrop onClose={() => setShowPrintModal(false)} maxWidth="sm" ariaLabel={t.tournament.printBouts}>
          <ModalHeader title={t.tournament.printBouts} />
          <div className="space-y-3">
            <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide">{t.common.days}</div>
            <div className="space-y-1.5">
              {[...new Set(bouts.map(b => b.bout_date ? String(b.bout_date).slice(0, 10) : '').filter((s): s is string => !!s))].sort().map(day => (
                <label key={day} className="flex items-center gap-2 cursor-pointer py-1.5">
                  <input type="checkbox" checked={printSelectedDays.includes(day)}
                    onChange={e => setPrintSelectedDays(prev => e.target.checked ? [...prev, day] : prev.filter(d => d !== day))}
                    className="w-4 h-4 rounded-sm accent-gray-900" />
                  <span className="text-sm font-medium">{formatDay(day, 'EEE, dd MMM')}</span>
                  <span className="text-xs text-gray-400 ml-auto">({bouts.filter(b => b.bout_date && String(b.bout_date).slice(0, 10) === day).length})</span>
                </label>
              ))}
            </div>
            {[...new Set(bouts.map(b => b.ring_name).filter((s): s is string => !!s))].length > 1 && (
              <>
                <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide">{t.common.rings}</div>
                <div className="space-y-1.5">
                  {[...new Set(bouts.map(b => b.ring_name).filter((s): s is string => !!s))].sort().map(ring => (
                    <label key={ring} className="flex items-center gap-2 cursor-pointer py-1.5">
                      <input type="checkbox" checked={printSelectedRings.includes(ring)}
                        onChange={e => setPrintSelectedRings(prev => e.target.checked ? [...prev, ring] : prev.filter(r => r !== ring))}
                        className="w-4 h-4 rounded-sm accent-gray-900" />
                      <span className="text-sm font-medium">{ring}</span>
                      <span className="text-xs text-gray-400 ml-auto">({bouts.filter(b => b.ring_name === ring).length})</span>
                    </label>
                  ))}
                </div>
              </>
            )}
          </div>
          <ModalFooter
            onSave={() => { setShowPrintModal(false); setTimeout(() => window.print(), 200); }}
            saveLabel={t.common.print}
            saveDisabled={!printSelectedDays.length}
            onCancel={() => setShowPrintModal(false)}
          />
        </ModalBackdrop>
      )}
      {/* Print-only: bouts grouped by day → ring */}
      {bouts.length > 0 && (() => {
        const allScored = bouts.every(b => b.status === 'scored');
        const filteredBouts = bouts.filter(b => {
          if (printSelectedDays.length > 0 && (!b.bout_date || !printSelectedDays.includes(String(b.bout_date).slice(0, 10)))) return false;
          if (printSelectedRings.length > 0 && b.ring_name && !printSelectedRings.includes(b.ring_name)) return false;
          return true;
        });
        return <BoutListPrint bouts={filteredBouts} allBouts={bouts} allScored={allScored} showStage={hasChampionshipPart(tournament.type)} />;
      })()}
      {bouts.length > 0 && (() => {
        const boutDays = [...new Set(boutsOrdered.map(b => b.bout_date ? String(b.bout_date).slice(0, 10) : null).filter(Boolean))];
        const pendingCount = boutsOrdered.filter(b => b.status !== 'scored').length;
        const scoredCount = boutsOrdered.filter(b => b.status === 'scored').length;
        return (
          <div className="flex gap-2 flex-wrap">
            {boutDays.length > 1 && boutDays.map(d => {
              const count = boutsOrdered.filter(b => b.bout_date && String(b.bout_date).slice(0, 10) === d).length;
              return (
                <button key={d} onClick={() => document.getElementById(`bouts-day-${d}`)?.scrollIntoView({ behavior: 'smooth' })}
                  className="badge badge-blue text-xs cursor-pointer hover:opacity-80">
                  {formatDay(d!, 'EEE dd MMM')} ({count})
                </button>
              );
            })}
            {pendingCount > 0 && scoredCount > 0 && (
              <>
                <button onClick={() => document.getElementById('bouts-pending')?.scrollIntoView({ behavior: 'smooth' })}
                  className="badge badge-amber text-xs cursor-pointer hover:opacity-80">
                  {statusLabel('pending')} ({pendingCount})
                </button>
                <button onClick={() => document.getElementById('bouts-scored')?.scrollIntoView({ behavior: 'smooth' })}
                  className="badge badge-gray text-xs cursor-pointer hover:opacity-80">
                  {statusLabel('scored')} ({scoredCount})
                </button>
              </>
            )}
          </div>
        );
      })()}
      {bouts.length > 0 && boutScoreState(bouts).open > 0 && (
        tournament.is_locked
          ? managesThisTournament && (
              <div className="card p-3 bg-gray-50 text-gray-500 text-xs flex items-start gap-2 no-print">
                <LockIcon className="w-4 h-4 shrink-0 mt-px" />
                <div>
                  <span className="font-semibold">{t.tournament.boutsLocked}.</span>{' '}
                  <span>{t.tournament.boutsLockedDetail}</span>
                </div>
              </div>
            )
          : (
              <div className="card p-3 bg-amber-50 border border-amber-200 text-amber-800 text-xs flex items-start gap-2 no-print">
                <HourglassIcon className="w-4 h-4 shrink-0 mt-px" />
                <div>
                  <div className="font-semibold">{t.tournament.boutsPlanning}</div>
                  <div className="text-amber-700 mt-0.5">{t.tournament.boutsPlanningDetail}</div>
                </div>
              </div>
            )
      )}
      {bouts.length === 0 && (
        <EmptyState compact testId="bouts-empty" title={t.tournament.noBouts}
          hint={managesThisTournament ? t.tournament.hintCreateBouts : undefined} />
      )}
      {/* The Pending chip scrolls here: the list starts with the live and the pending bouts. */}
      <div id="bouts-pending" className="space-y-2 no-print scroll-mt-20">
        {(() => { let lastBoutDate = ''; return boutsOrdered.map((bout, idx) => {
          const juryForBout = juryAssignments.find(j => j.bout_id === bout.id);
          // A list row has no jury_id: the jury of the bout is the assignment (#664, F27).
          const seats = boutSeatsOf(bout, user?.id, juryForBout?.jury_id ?? null);
          const showDivider = idx === firstScoredIdx && idx > 0;
          const boutDateStr = bout.bout_date ? String(bout.bout_date).slice(0, 10) : '';
          const showDayDivider = boutDateStr && boutDateStr !== lastBoutDate;
          if (showDayDivider) lastBoutDate = boutDateStr;
          const sideJudgeCount = Array.isArray(bout.side_judges) ? bout.side_judges.length : null;
          const showOfficials = canOfficiate || isJury || managesThisTournament;

          const ageGroup    = bout.bout_age_group    || deriveAgeGroup(bout.red_age_group, bout.blue_age_group);
          const weightClass = bout.bout_weight_class || deriveWeightClass(bout.red_weight_class, bout.blue_weight_class);
          const timeFormula = roundFormula(ageGroup, bout.rounds); // #602: 3 or 5 rounds

          return (
            <div key={bout.id}>
              {showDayDivider && (
                <div id={`bouts-day-${boutDateStr}`} className="flex items-center gap-3 py-3 scroll-mt-20">
                  <div className="flex-1 h-px bg-blue-200" />
                  <span className="text-xs font-bold uppercase tracking-wide text-blue-500 inline-flex items-center gap-1.5">
                    <CalendarIcon className="w-3.5 h-3.5" /> {formatDay(boutDateStr, 'EEEE, dd MMM yyyy')}
                  </span>
                  <span className="text-[10px] text-blue-400">
                    {/* #559 — the count in the language of the page ("1 kova"), not "1 bouts". */}
                    ({boutCount(boutsOrdered.filter(b => b.bout_date && String(b.bout_date).slice(0, 10) === boutDateStr).length)})
                  </span>
                  <div className="flex-1 h-px bg-blue-200" />
                </div>
              )}
              {showDivider && (
                <div id="bouts-scored" className="flex items-center gap-3 py-2 scroll-mt-20">
                  <div className="flex-1 h-px bg-gray-200" />
                  <span className="text-xs font-semibold uppercase tracking-wide text-gray-400">{t.common.scored}</span>
                  <div className="flex-1 h-px bg-gray-200" />
                </div>
              )}
              <div id={`bout-${bout.id}`} className={`card overflow-hidden scroll-mt-20 bout-card-screen ${bout.status === 'current' ? 'ring-2 ring-green-500' : ''}`}>
                <Link to={`/bouts/${bout.id}`} state={listFrom} className="block hover:bg-gray-50 transition-colors">
                  {/* Top: bout number + boxers full width */}
                  {/* #552 — a name is the main information of the card. On a
                      phone the names wrap and never cut to a few letters: the
                      first name and the team get their own lines, and the
                      side columns are narrower. */}
                  <div className="px-3 sm:px-4 pt-4 pb-2 flex items-start gap-2 sm:gap-3">
                    <div className="flex flex-col items-center gap-1 shrink-0">
                      <div className="w-8 h-8 sm:w-9 sm:h-9 rounded-lg bg-gray-100 flex items-center justify-center font-bold text-sm">{bout.bout_number}</div>
                      {managesThisTournament && bout.status === 'pending' && (
                        <button onClick={e => { e.preventDefault(); e.stopPropagation(); setBoutFormMode('edit'); setBoutFormTarget(bout); setBoutFormOpen(true); }}
                          className="btn-icon" title={t.common.edit} aria-label={t.common.edit} data-testid={`bout-edit-${bout.id}`}>
                          <PencilIcon className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1.5">
                        <div className="flex-1 min-w-0 text-left bout-name-stack">
                          {bout.red_first_name ? (
                            <>
                              {/* #479 — the last name on top in large letters, as on the
                                  bout page and the Ring TV; the first name below it. */}
                              <div className="font-black text-red-700 text-base sm:text-lg leading-tight break-words">{bout.red_last_name}</div>
                              <div className="flex flex-col sm:flex-row sm:items-baseline sm:gap-1">
                                <span className="font-bold text-red-700 text-sm sm:text-base leading-tight break-words opacity-70 min-w-0">{bout.red_first_name}</span>
                                {bout.red_team_name && <span className="text-[11px] text-gray-500 font-medium truncate sm:ml-auto">{bout.red_team_name}</span>}
                              </div>
                            </>
                          ) : bout.red_source_bout_id ? (
                            <div className="text-xs text-gray-400 italic">
                              {t.common.boutWinner.replace('{n}', String(bouts.find(b => b.id === bout.red_source_bout_id)?.bout_number ?? '?'))}
                            </div>
                          ) : (
                            <div className="text-xs text-gray-300">{t.common.tbd}</div>
                          )}
                        </div>
                        <div className="text-[10px] text-gray-300 font-light shrink-0 px-0.5 sm:px-1">VS</div>
                        <div className="flex-1 min-w-0 text-right bout-name-stack">
                          {bout.blue_first_name ? (
                            <>
                              <div className="font-black text-blue-700 text-base sm:text-lg leading-tight break-words">{bout.blue_last_name}</div>
                              {/* The first name comes first in the code, so on a phone
                                  it stands above the team; row-reverse puts the team on
                                  the left on a wide screen. */}
                              <div className="flex flex-col sm:flex-row-reverse sm:items-baseline sm:gap-1">
                                <span className="font-bold text-blue-700 text-sm sm:text-base leading-tight break-words opacity-70 min-w-0">{bout.blue_first_name}</span>
                                {bout.blue_team_name && <span className="text-[11px] text-gray-500 font-medium truncate sm:mr-auto">{bout.blue_team_name}</span>}
                              </div>
                            </>
                          ) : bout.blue_source_bout_id ? (
                            <div className="text-xs text-gray-400 italic">
                              {t.common.boutWinner.replace('{n}', String(bouts.find(b => b.id === bout.blue_source_bout_id)?.bout_number ?? '?'))}
                            </div>
                          ) : (
                            <div className="text-xs text-gray-300">{t.common.tbd}</div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                  {/* Bottom: pills left, outcome+status right */}
                  <div className="px-3 sm:px-4 pb-3 pt-0 flex items-start gap-2">
                    <div className="flex-1 min-w-0 flex flex-wrap items-center gap-0.5">
                      {bout.ring_name && <span className={`badge badge-gray ${BADGE_XS} px-1`}>{bout.ring_name}</span>}
                      {/* #458 — a bout with no stage has no stage pill. `|| 'Challenge'`
                          labelled a manual championship bout as an open-ring one. */}
                      <StagePill stage={bout.stage} size="xs" />
                      {(bout.red_gender || bout.blue_gender) && (
                        <GenderBadge gender={(bout.red_gender || bout.blue_gender || '').toLowerCase()} short size="xs" className="px-1" />
                      )}
                      {ageGroup && <span className={`badge badge-gray ${BADGE_XS} px-1 gap-0.5`}><span className="opacity-50 font-normal">{t.bout.ageGroupShort}</span><span>{ageGroup}</span></span>}
                      {weightClass && <span className={`badge badge-gray ${BADGE_XS} px-1 gap-0.5`}><span className="opacity-50 font-normal">{t.bout.weightClassShort}</span><span>{weightClass}</span></span>}
                      {timeFormula && <span className={`badge badge-gray ${BADGE_XS} px-1 gap-0.5 inline-flex items-center`}><ClockIcon className="w-2.5 h-2.5 opacity-50" /><span>{timeFormula}</span></span>}
                      {/* #622 — the level, for the roles of the Officials menu (the server decides). */}
                      {bout.level && <BoutLevelPill level={bout.level} estimated={bout.experience_estimated} size="xs" />}
                      {bout.bout_date && <span className={`badge badge-gray ${BADGE_XS} px-1`}>{formatDay(bout.bout_date, 'EEE d')}</span>}
                      {showOfficials && sideJudgeCount !== null && (
                        <span className={`badge ${BADGE_XS} px-1 ${sideJudgeCount >= MIN_SIDE_JUDGES ? 'badge-gray' : 'badge-amber'}`}>{sideJudgeCount}/{MAX_SIDE_JUDGES}</span>
                      )}
                      {seats.any && (
                        <span className="inline-flex items-center whitespace-nowrap text-[10px] font-bold text-indigo-700 bg-indigo-50 px-1.5 py-0.5 rounded-full border border-indigo-200">
                          {seats.jury ? t.bout.jury : seats.ringReferee ? t.bout.ringReferee : t.bout.sideJudge}
                        </span>
                      )}
                    </div>
                    <div className="flex flex-col items-end gap-1 shrink-0">
                      {bout.verdict_winner && (
                        // #552 — the outcome is its own element: in an inline-flex
                        // badge a text node lost its first space ("Red· CCL").
                        <span className={`badge gap-1 ${BADGE_XS} ${bout.verdict_winner === 'red' ? 'bg-red-100 text-red-700 border-red-200' : bout.verdict_winner === 'blue' ? 'bg-blue-100 text-blue-700 border-blue-200' : 'badge-gray'}`}>
                          {bout.verdict_outcome === 'DRAW' ? 'DRAW' : (
                            <>
                              {bout.verdict_winner === 'red'
                                ? <span className="inline-flex items-center gap-1"><DotIcon className="w-5 h-5 text-red-500" />{t.common.red}</span>
                                : bout.verdict_winner === 'blue'
                                ? <span className="inline-flex items-center gap-1"><DotIcon className="w-5 h-5 text-blue-500" />{t.common.blue}</span>
                                : t.common.draw}
                              {bout.verdict_outcome && <span>· {bout.verdict_outcome}</span>}
                            </>
                          )}
                        </span>
                      )}
                      <div className="flex items-center gap-2">
                        {bout.status === 'pending' && (
                          <button
                            onClick={async (e) => {
                              e.preventDefault(); e.stopPropagation();
                              // #515 — a guest gets the sign-in prompt, not a jump to the manual.
                              if (!user) { setShowGuestBell(true); return; }
                              // #593 — the new state at once; a second tap waits for the answer.
                              if (bellBusy.current.has(bout.id)) return;
                              const next = !boutSubs.has(bout.id);
                              bellBusy.current.add(bout.id);
                              // A reload of the list that started before the answer applies the tap again.
                              const settled = setBoutSub(bout.id, next);
                              try {
                                if (next) await followBout(bout.id);
                                else await unfollowBout(bout.id);
                              } catch (err) {
                                setBoutSub(bout.id, !next)();
                                showError(err);
                              } finally {
                                settled();
                                bellBusy.current.delete(bout.id);
                              }
                            }}
                            data-testid="bout-bell"
                            title={boutSubs.has(bout.id) ? t.common.notifyOn : t.common.notifyMe}
                            aria-label={boutSubs.has(bout.id) ? t.common.notifyOn : t.common.notifyMe}
                            // #552 — p-2 -m-2: a 36 px touch target, the same layout.
                            className={`p-2 -m-2 transition-colors ${
                              boutSubs.has(bout.id)
                                ? 'text-amber-500 hover:text-amber-600'
                                : 'text-gray-300 hover:text-gray-500'
                            }`}>
                            <BellIcon className="w-5 h-5" filled={boutSubs.has(bout.id)} />
                          </button>
                        )}
                        <BoutStatusPill status={bout.status} />
                      </div>
                    </div>
                  </div>
                </Link>
              </div>
            </div>
          );
        }); })()}
      </div>

      {/* Unified Create / Edit Bout Modal */}
      {boutFormOpen && (
        <BoutFormModal
          mode={boutFormMode}
          bout={boutFormMode === 'edit' ? (boutFormTarget ?? undefined) : undefined}
          tournamentId={id}
          tournamentType={tournament.type}
          rings={rings}
          referees={referees}
          juryPool={referees.filter(r => r.in_jury_pool)}
          registrations={registrations}
          bouts={bouts}
          tournamentDays={tournamentDays}
          isMultiDay={isMultiDay}
          ageGroups={ageGroups}
          onDone={async (warnings) => {
            // #605 — the write committed: close the modal and give the result
            // first, as the bout page does. Before, the refresh came first: a
            // failed GET kept the modal open with no result, and Save was enabled again.
            setBoutFormOpen(false);
            setBoutFormTarget(null);
            // If the backend attached non-blocking advisories (e.g. rest-gap),
            // they show in the amber-warning channel INSTEAD of the success
            // text, so the TM actually sees them rather than a green check.
            // #419 — a failed refresh is a warning, not an error, and it keeps
            // the advice. `reload()` writes the bouts under the order guard of
            // the other writers (`boutsSeq`), and it reads the jury too.
            await afterWrite({
              source: 'BoutsTab:bout-form',
              success: warnings?.length ? undefined : (boutFormMode === 'create' ? t.tournament.boutCreated : t.tournament.boutUpdated),
              warnings,
              refresh: reload,
            });
          }}
          onClose={() => { setBoutFormOpen(false); setBoutFormTarget(null); }}
          onDelete={handleDeleteBout}
        />
      )}
      {showGuestBell && <GuestBellPrompt onClose={() => setShowGuestBell(false)} />}
    </div>
  );
}
